/**
 * JDImportModal - Import a job description via paste, file upload, or Google Drive.
 * Parses the JD text on the backend and lets the user preview/apply extracted fields.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TeamPosition } from '../../types/team';
import { parseImportedJd, uploadJdFile, JdParseResult } from '../../services/teamApi';

type InputMode = 'paste' | 'upload' | 'drive';
type ApplyMode = 'merge' | 'overwrite';
type TargetMode = 'update' | 'create_new';

interface JDImportModalProps {
  teamId: number;
  getTeamId?: () => Promise<number>;
  isEditMode?: boolean;
  onApply: (extracted: Partial<TeamPosition>, mode: ApplyMode) => void;
  onCreateNew?: (extracted: Partial<TeamPosition>) => void;
  onClose: () => void;
  onDrivePick?: () => Promise<{ text?: string; file?: File; name: string; mime: string } | null>;
}

const FIELD_LABELS: Record<string, string> = {
  role: 'Role / Job Title',
  description: 'Description',
  skills_required: 'Required Skills',
  skills_nice_to_have: 'Nice-to-Have Skills',
  responsibilities: 'Responsibilities',
  experience_level: 'Experience Level',
  management_level: 'Management Level',
  education_level: 'Education Level',
  education_importance: 'Education Importance',
  employment_type: 'Employment Type',
  location_model: 'Work Model',
  location: 'Location',
  allocation_percentage: 'Allocation %',
  priority: 'Priority',
  count_needed: 'Headcount',
};

function confidenceColor(score: number): string {
  if (score >= 80) return 'text-green-400';
  if (score >= 50) return 'text-yellow-400';
  return 'text-red-400';
}

function formatValue(value: any): string {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'number') return String(value);
  return String(value || '');
}

export const JDImportModal: React.FC<JDImportModalProps> = ({
  teamId,
  getTeamId,
  isEditMode = false,
  onApply,
  onCreateNew,
  onClose,
  onDrivePick,
}) => {
  const resolveTeamId = async () => {
    if (teamId > 0) return teamId;
    if (getTeamId) return getTeamId();
    throw new Error('No team available. Please save the team first.');
  };
  const [inputMode, setInputMode] = useState<InputMode>('paste');
  const [pasteText, setPasteText] = useState('');
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [parseResult, setParseResult] = useState<JdParseResult | null>(null);
  const [applyMode, setApplyMode] = useState<ApplyMode>(isEditMode ? 'overwrite' : 'merge');
  const [targetMode, setTargetMode] = useState<TargetMode>('update');
  const [showConfirmOverwrite, setShowConfirmOverwrite] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const VALID_EXTENSIONS = ['.txt', '.md', '.html', '.pdf', '.docx', '.doc'];
  const VALID_MIME_TYPES = [
    'text/plain', 'text/markdown', 'text/html', 'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
  ];
  const SERVER_SIDE_EXTENSIONS = ['.pdf', '.docx', '.doc'];

  const isServerSideFile = (ext: string, mime: string) =>
    SERVER_SIDE_EXTENSIONS.includes(ext) ||
    mime === 'application/pdf' ||
    mime.includes('wordprocessingml') ||
    mime === 'application/msword';

  const handleFileDrop = useCallback(async (files: FileList) => {
    const file = files[0];
    if (!file) return;
    const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
    if (!VALID_MIME_TYPES.includes(file.type) && !VALID_EXTENSIONS.includes(ext)) {
      setError('Unsupported file type. Please upload .txt, .md, .html, .docx, or .pdf files.');
      return;
    }
    try {
      if (isServerSideFile(ext, file.type)) {
        setLoading(true);
        setError('');
        setFileName(file.name);
        setInputMode('upload');
        const tid = await resolveTeamId();
        const result = await uploadJdFile(tid, file);
        setParseResult(result);
        setLoading(false);
        return;
      }
      const text = await readFileAsText(file);
      if (!text.trim()) {
        setError('The file appears to be empty or could not be read.');
        return;
      }
      setPasteText(text);
      setFileName(file.name);
      setInputMode('upload');
      setError('');
    } catch (err: any) {
      setLoading(false);
      setError(err.message || 'Failed to read file. Try pasting the text instead.');
    }
  }, [teamId]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFileDrop(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileDrop(e.target.files);
    }
  };

  const handleDrivePick = async () => {
    if (!onDrivePick) return;
    setLoading(true);
    setError('');
    try {
      const result = await onDrivePick();
      if (result) {
        if (result.file) {
          const ext = '.' + (result.name.split('.').pop()?.toLowerCase() || '');
          if (isServerSideFile(ext, result.mime)) {
            setFileName(result.name);
            setInputMode('drive');
            const tid = await resolveTeamId();
            const parseRes = await uploadJdFile(tid, result.file);
            setParseResult(parseRes);
          } else {
            const text = await readFileAsText(result.file);
            setPasteText(text);
            setFileName(result.name);
            setInputMode('drive');
          }
        } else if (result.text !== undefined) {
          setPasteText(result.text);
          setFileName(result.name);
          setInputMode('drive');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to pick file from Drive');
    } finally {
      setLoading(false);
    }
  };

  const handleParse = async () => {
    if (!pasteText.trim()) {
      setError('Please provide some JD text to parse');
      return;
    }
    setLoading(true);
    setError('');
    setParseResult(null);
    try {
      const tid = await resolveTeamId();
      const result = await parseImportedJd(tid, {
        text: pasteText,
        source: inputMode === 'paste' ? 'paste' : inputMode === 'drive' ? 'drive' : 'upload',
        file_meta: fileName ? { name: fileName } : undefined,
      });
      setParseResult(result);
    } catch (err: any) {
      setError(err.message || 'Failed to parse job description');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!parseResult) return;
    if (targetMode === 'create_new' && onCreateNew) {
      onCreateNew(parseResult.extracted);
      return;
    }
    if (applyMode === 'overwrite' && !showConfirmOverwrite) {
      setShowConfirmOverwrite(true);
      return;
    }
    onApply(parseResult.extracted, applyMode);
  };

  const extractedFields = parseResult
    ? Object.entries(parseResult.extracted).filter(([_, v]) => v !== undefined && v !== null && (Array.isArray(v) ? v.length > 0 : true))
    : [];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-dark-card border border-dark-border rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border">
          <h3 className="text-lg font-bold text-dark-text">Import Job Description</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-gray-200 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {!parseResult ? (
            <>
              {/* Input area */}
              <div
                ref={dropZoneRef}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-lg transition-colors ${
                  isDragOver ? 'border-indigo-500 bg-indigo-500/10' : 'border-dark-border'
                }`}
              >
                <textarea
                  value={pasteText}
                  onChange={e => { setPasteText(e.target.value); setInputMode('paste'); setFileName(''); }}
                  placeholder="Paste job description text here, or drag & drop a file..."
                  rows={12}
                  className="w-full bg-transparent px-4 py-3 text-sm text-dark-text placeholder:text-gray-500 outline-none resize-y"
                />
                {isDragOver && (
                  <div className="absolute inset-0 flex items-center justify-center bg-indigo-500/10 rounded-lg pointer-events-none">
                    <span className="text-indigo-400 font-medium">Drop file here</span>
                  </div>
                )}
              </div>

              {/* File info */}
              {fileName && (
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {fileName}
                  <button onClick={() => { setFileName(''); setPasteText(''); }} className="text-red-400 hover:text-red-300 ml-1">&times;</button>
                </div>
              )}

              {/* Action buttons row */}
              <div className="flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.md,.html,.docx,.doc,.pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-1.5 bg-dark-bg border border-dark-border hover:border-indigo-500 text-gray-300 hover:text-white text-sm rounded-lg transition-colors"
                >
                  Upload File
                </button>
                {onDrivePick && (
                  <button
                    onClick={handleDrivePick}
                    disabled={loading}
                    className="px-3 py-1.5 bg-dark-bg border border-dark-border hover:border-indigo-500 text-gray-300 hover:text-white text-sm rounded-lg transition-colors disabled:opacity-50"
                  >
                    Google Drive
                  </button>
                )}
                <div className="flex-1" />
                <span className="text-xs text-gray-500">
                  {pasteText.length > 0 ? `${pasteText.length.toLocaleString()} chars` : ''}
                </span>
              </div>

              {error && (
                <div className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg p-3">
                  {error}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Parse results preview */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-dark-text">Extracted Fields</h4>
                  <span className="text-xs text-gray-400">{extractedFields.length} fields detected</span>
                </div>

                {parseResult.warnings.length > 0 && (
                  <div className="text-yellow-400 text-xs bg-yellow-900/20 border border-yellow-800 rounded-lg p-3 space-y-1">
                    {parseResult.warnings.map((w, i) => (
                      <p key={i}>{w}</p>
                    ))}
                  </div>
                )}

                <div className="bg-dark-bg border border-dark-border rounded-lg divide-y divide-dark-border">
                  {extractedFields.map(([field, value]) => {
                    const conf = parseResult.confidence[field];
                    return (
                      <div key={field} className="flex items-start gap-3 px-4 py-2.5">
                        <span className="text-xs text-gray-400 w-36 flex-shrink-0 pt-0.5">
                          {FIELD_LABELS[field] || field}
                        </span>
                        <span className="text-sm text-dark-text flex-1 break-words">
                          {formatValue(value)}
                        </span>
                        {conf !== undefined && (
                          <span className={`text-xs font-medium flex-shrink-0 ${confidenceColor(conf)}`}>
                            {conf}%
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {extractedFields.length === 0 && (
                  <p className="text-gray-500 text-sm italic py-4 text-center">
                    No fields could be extracted. The text may not be a recognizable job description.
                  </p>
                )}

                {/* Target & Apply mode selector */}
                {extractedFields.length > 0 && (
                  <div className="space-y-3 pt-2">
                    {/* Target: update current vs create new */}
                    {isEditMode && onCreateNew && (
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-gray-400">Target:</span>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="targetMode"
                            checked={targetMode === 'update'}
                            onChange={() => { setTargetMode('update'); setShowConfirmOverwrite(false); }}
                            className="accent-indigo-500"
                          />
                          <span className="text-sm text-gray-300">Update this position</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="targetMode"
                            checked={targetMode === 'create_new'}
                            onChange={() => { setTargetMode('create_new'); setShowConfirmOverwrite(false); }}
                            className="accent-indigo-500"
                          />
                          <span className="text-sm text-gray-300">Create new position</span>
                        </label>
                      </div>
                    )}

                    {/* Apply mode (only relevant when updating) */}
                    {targetMode === 'update' && (
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-gray-400">Fields:</span>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="applyMode"
                            checked={applyMode === 'overwrite'}
                            onChange={() => { setApplyMode('overwrite'); setShowConfirmOverwrite(false); }}
                            className="accent-indigo-500"
                          />
                          <span className="text-sm text-gray-300">Replace all fields</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="applyMode"
                            checked={applyMode === 'merge'}
                            onChange={() => { setApplyMode('merge'); setShowConfirmOverwrite(false); }}
                            className="accent-indigo-500"
                          />
                          <span className="text-sm text-gray-300">Fill empty fields only</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}

                {showConfirmOverwrite && (
                  <div className="text-yellow-400 text-sm bg-yellow-900/20 border border-yellow-800 rounded-lg p-3">
                    This will replace all fields including the job title. Click "Apply" again to confirm.
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-dark-border">
          {parseResult ? (
            <>
              <button
                onClick={() => { setParseResult(null); setError(''); setShowConfirmOverwrite(false); }}
                className="text-sm text-gray-400 hover:text-gray-200 transition-colors"
              >
                &larr; Back to text
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  disabled={extractedFields.length === 0}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {showConfirmOverwrite
                    ? 'Confirm Replace'
                    : targetMode === 'create_new'
                      ? 'Create New Position'
                      : 'Apply to Position'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div />
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleParse}
                  disabled={loading || !pasteText.trim()}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Parsing...
                    </span>
                  ) : (
                    'Parse JD'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default JDImportModal;
